import React from "react";
import ReactDOM from "react-dom/client";
import "./tailwind.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { AppProvider } from "./context/AppContext";

// Comprehensive error suppression for ResizeObserver and React 19 ref issues
const errorHandler = (e) => {
  const errorMessage = e.message || '';
  
  // Suppress ResizeObserver errors
  if (errorMessage.includes('ResizeObserver')) {
    e.stopImmediatePropagation();
    e.preventDefault();
    return false;
  }
  
  // Suppress React 19 ref warnings from Ant Design
  if (errorMessage.includes('element.ref was removed in React 19') ||
      errorMessage.includes('ref is now a regular prop')) {
    e.stopImmediatePropagation();
    e.preventDefault();
    return false;
  }
};

// Handle errors in multiple ways.
// `capture: true` is critical  CRA's dev error overlay subscribes during
// runtime injection, before our app code runs. Without the capture phase,
// our handler fires AFTER the overlay's, so stopImmediatePropagation never
// reaches the overlay and the modal still pops. Capture phase runs on the
// way down the DOM, before any bubble-phase listeners.
window.addEventListener('error', errorHandler, true);
window.addEventListener(
  'unhandledrejection',
  (event) => {
    const errorMessage = event.reason?.message || '';
    if (
      errorMessage.includes('ResizeObserver') ||
      errorMessage.includes('element.ref was removed in React 19')
    ) {
      event.stopImmediatePropagation();
      event.preventDefault();
    }
  },
  true,
);

// Override console.error and console.warn to suppress known issues
const originalConsoleError = console.error;
const originalConsoleWarn = console.warn;

console.error = (...args) => {
  const message = args.join(' ');
  if (message.includes('ResizeObserver') || 
      message.includes('element.ref was removed in React 19') ||
      message.includes('ref is now a regular prop')) {
    return;
  }
  originalConsoleError.apply(console, args);
};

console.warn = (...args) => {
  const message = args.join(' ');
  if (message.includes('element.ref was removed in React 19') ||
      message.includes('ref is now a regular prop')) {
    return;
  }
  originalConsoleWarn.apply(console, args);
};

// Patch ResizeObserver to catch errors at the source
const OriginalResizeObserver = window.ResizeObserver;
window.ResizeObserver = class extends OriginalResizeObserver {
  constructor(callback) {
    super((entries, observer) => {
      window.requestAnimationFrame(() => {
        try {
          callback(entries, observer);
        } catch (error) {
          if (error.message && error.message.includes('ResizeObserver loop')) {
            return;
          }
          throw error;
        }
      });
    });
  }
};

// Suppress React error overlay for ResizeObserver
if (typeof window !== 'undefined') {
  window.__REACT_ERROR_OVERLAY_GLOBAL_HOOK__ = window.__REACT_ERROR_OVERLAY_GLOBAL_HOOK__ || {};
  const originalCaptureError = window.__REACT_ERROR_OVERLAY_GLOBAL_HOOK__.captureError;
  window.__REACT_ERROR_OVERLAY_GLOBAL_HOOK__.captureError = function(error) {
    if (error && error.message && error.message.includes('ResizeObserver')) {
      return;
    }
    if (originalCaptureError) {
      originalCaptureError.call(this, error);
    }
  };
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <AppProvider>
      <App />
    </AppProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
